import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useDataStore } from "@/lib/store/data-store";
import type { Unit } from "@/lib/types/unit";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { normalizeSearchText, getDescriptorSearchText } from "@/lib/utils/search-normalize";

interface UnitSearchProps {
  onSelect?: (unit: Unit) => void;
  selectedUnits?: Unit[];
  showImages?: boolean;
  showSelectedUnits?: boolean;
  onRemove?: (unit: Unit) => void;
  placeholder?: string;
}

export function UnitSearch({
  onSelect,
  selectedUnits = [],
  showImages = false,
  showSelectedUnits = false,
  onRemove,
  placeholder = "Search for unit",
}: UnitSearchProps = {}) {
  const navigate = useNavigate();
  const { units, loadingUnits } = useDataStore();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; position: "above" | "below" } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const wasManuallyClosedRef = useRef(false);

  // Get selected descriptor IDs for exclusion
  const selectedDescriptorIds = useMemo(() => {
    return new Set(selectedUnits.map((u) => u.descriptorName));
  }, [selectedUnits]);

  // Filter units based on query, excluding selected units if provided
  const filteredUnits = useMemo(() => {
    if (!units || !query.trim()) return [];
    
    const normalizedQuery = normalizeSearchText(query);
    return units
      .filter((unit) => {
        // Exclude already selected units if selectedUnits prop is provided
        if (selectedUnits.length > 0 && selectedDescriptorIds.has(unit.descriptorName)) {
          return false;
        }
        
        const normalizedName = normalizeSearchText(unit.name);
        const normalizedDescriptor = normalizeSearchText(getDescriptorSearchText(unit.descriptorName));
        const nameMatch = normalizedName.includes(normalizedQuery);
        const descriptorMatch = normalizedDescriptor.includes(normalizedQuery);
        return nameMatch || descriptorMatch;
      })
      .slice(0, 10); // Limit to 10 results
  }, [units, query, selectedUnits, selectedDescriptorIds]);

  // Reset selected index only when query changes (not when navigating with arrow keys)
  const prevQueryRef = useRef(query);
  useEffect(() => {
    // Only reset if the query actually changed, not if filteredUnits array reference changed
    if (prevQueryRef.current !== query) {
      setSelectedIndex(0);
      prevQueryRef.current = query;
    }
  }, [query]);

  // Scroll selected item into view when navigating with keyboard
  useEffect(() => {
    if (isOpen && dropdownRef.current && filteredUnits.length > 0 && selectedIndex >= 0 && selectedIndex < filteredUnits.length) {
      // Use setTimeout to ensure DOM is updated
      const timer = setTimeout(() => {
        const selectedItem = itemRefs.current.get(selectedIndex);
        if (selectedItem) {
          selectedItem.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [selectedIndex, isOpen, filteredUnits.length]);

  // Show dropdown when there are results, but only if it wasn't manually closed
  useEffect(() => {
    if (query.trim().length > 0 && filteredUnits.length > 0 && !wasManuallyClosedRef.current) {
      setIsOpen(true);
    } else if (query.trim().length === 0) {
      setIsOpen(false);
      wasManuallyClosedRef.current = false;
    }
  }, [query, filteredUnits]);

  // Calculate dropdown position based on input element with smart above/below detection
  useEffect(() => {
    const calculateDropdownPosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownMaxHeight = showImages ? 400 : 300;
        const gap = 4; // Gap between input and dropdown
        const spaceBelow = viewportHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;
        
        // Determine if we should position above or below
        // Position above if there's not enough space below but more space above
        const shouldPositionAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
        
        let top: number;
        if (shouldPositionAbove) {
          // Position above: use available space above, but don't go above viewport
          const availableSpace = Math.max(0, spaceAbove);
          top = Math.max(8, rect.top - Math.min(availableSpace, dropdownMaxHeight) - gap);
        } else {
          // Position below: use available space below
          top = rect.bottom + gap;
        }
        
        setDropdownPosition({
          top,
          left: rect.left,
          width: rect.width,
          position: shouldPositionAbove ? "above" : "below",
        });
      }
    };

    if (isOpen && inputRef.current) {
      calculateDropdownPosition();
      
      // Update position on scroll and resize
      window.addEventListener("scroll", calculateDropdownPosition, true);
      window.addEventListener("resize", calculateDropdownPosition);
      
      return () => {
        window.removeEventListener("scroll", calculateDropdownPosition, true);
        window.removeEventListener("resize", calculateDropdownPosition);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen, showImages]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        wasManuallyClosedRef.current = true;
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle unit selection - use callback if provided, otherwise navigate
  const handleUnitSelect = (unit: Unit) => {
    if (onSelect) {
      onSelect(unit);
    } else {
      navigate(`/units/${encodeURIComponent(unit.descriptorName)}`);
    }
    setQuery("");
    setIsOpen(false);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle arrow keys and enter even if dropdown isn't open yet but there are filtered results
    if (filteredUnits.length > 0) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          if (!isOpen) {
            setIsOpen(true);
          }
          setSelectedIndex((prev) => (prev + 1) % filteredUnits.length);
          return;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          if (!isOpen) {
            setIsOpen(true);
          }
          setSelectedIndex((prev) => (prev - 1 + filteredUnits.length) % filteredUnits.length);
          return;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          const selectedUnit = filteredUnits[selectedIndex];
          if (selectedUnit) {
            handleUnitSelect(selectedUnit);
          } else if (filteredUnits.length > 0) {
            // If no index selected but there are results, select the first one
            handleUnitSelect(filteredUnits[0]!);
          }
          return;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          setQuery("");
          wasManuallyClosedRef.current = true;
          return;
      }
    }

    // Handle Enter for exact match when dropdown is closed
    if (e.key === "Enter" && query.trim() && filteredUnits.length === 0) {
      const normalizedQuery = normalizeSearchText(query);
      const exactMatch = units?.find(
        (unit) =>
          (selectedUnits.length === 0 || !selectedDescriptorIds.has(unit.descriptorName)) &&
          (normalizeSearchText(unit.name) === normalizedQuery ||
            normalizeSearchText(getDescriptorSearchText(unit.descriptorName)) === normalizedQuery)
      );
      if (exactMatch) {
        handleUnitSelect(exactMatch);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    // Reset the manually closed flag when user types, so dropdown can open again
    if (newQuery.trim().length > 0) {
      wasManuallyClosedRef.current = false;
    }
  };

  const handleResultClick = (unit: Unit) => {
    handleUnitSelect(unit);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            wasManuallyClosedRef.current = false;
            if (query.trim().length > 0 && filteredUnits.length > 0) {
              setIsOpen(true);
            }
          }}
          className="w-full pr-10"
        />
        {loadingUnits && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {isOpen && filteredUnits.length > 0 && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className={cn(
            "fixed z-[60] bg-popover text-popover-foreground rounded-md border shadow-md overflow-y-auto",
            showImages ? "max-h-[400px]" : "max-h-[300px]"
          )}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          {filteredUnits.map((unit, index) => {
            const isSelected = index === selectedIndex;
            return (
              <button
                key={unit.descriptorName}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(index, el);
                  } else {
                    itemRefs.current.delete(index);
                  }
                }}
                type="button"
                onClick={() => handleResultClick(unit)}
                className={cn(
                  "w-full text-left transition-colors flex items-center gap-3 border-0",
                  showImages ? "px-4 py-3" : "px-4 py-2",
                  isSelected 
                    ? "bg-accent text-accent-foreground" 
                    : "hover:bg-accent/50 hover:text-accent-foreground"
                )}
                aria-selected={isSelected}
                data-selected={isSelected ? "true" : "false"}
              >
              {showImages && (
                <img
                  src={getUnitImagePath(unit.descriptorName)}
                  alt={unit.name}
                  className="w-12 h-12 object-contain flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <div className={cn("flex-1", showImages && "min-w-0")}>
                <div className={cn("font-medium", showImages && "truncate")}>{unit.name}</div>
                <div className={cn("text-xs text-muted-foreground", showImages && "truncate")}>{unit.descriptorName}</div>
              </div>
            </button>
            );
          })}
        </div>,
        document.body
      )}

      {/* Selected units display */}
      {showSelectedUnits && selectedUnits.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {selectedUnits.map((unit) => (
            <div
              key={unit.descriptorName}
              className="relative flex flex-col items-center gap-2 p-3 border rounded-lg bg-card"
            >
              {onRemove && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={() => onRemove(unit)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
              <img
                src={getUnitImagePath(unit.descriptorName)}
                alt={unit.name}
                className="w-20 h-20 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <h5 className="text-sm font-medium text-center mt-1">{unit.name}</h5>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

